import { Component, Inject, Prop } from 'vue-property-decorator';
import ApeironComponent from '../commons/apeiron-component.model';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';

@Component
export default class FechasTutoriasComponent extends ApeironComponent {
  @Inject('solicitudService') public readonly solicitudService: () => SolicitudService;

  @Prop({ required: false })
  public solucion: Solucion;

  @Prop({ required: false })
  public solicitud: Solicitud;

  public propOrder = 'estado';
  public reverse = false;

  public isFetching = false;
  form = this.solicitudModel;

  public periodoBecasValidas = false;
  public duracionMovilidadMeses: number;
  public duracionBecaMeses = 0;
  public pasosPreviosCaptura = false;

  private readonly FECHA_PERIODO_TUTORIA_INICIO = `${new Date().getUTCFullYear()}-07-01`;
  private readonly FECHA_PERIODO_tUTORIA_FIN = `${new Date().getUTCFullYear()}-12-31`;

  /**
   * Se inicializa model de form
   */
  private get solicitudModel() {
    return {
      solicitud: {
        beca: {
          periodoBeca: {
            fechaInicioBeca: '',
            fechaFinBeca: '',
          },
        },
        estadoActual: '',
      },
      tags: [],
    };
  }

  created() {
    this.form.tags = this.solucion.tags;
    this.getSolicitudVigente();
  }

  /**
   * Obtiene la solicitud vigente desde el servicio y actualiza el formulario con sus datos.
   * También calcula la duración de la beca en meses y busca apoyos complementarios asociados.
   * @async
   * @throws Lanza un error si `solicitudService().find` falla.
   */
  async getSolicitudVigente() {
    this.isFetching = true;

    try {
      const res = await this.solicitudService().find(this.solicitud.id);

      this.pasosPreviosCaptura = !!res?.beca?.id;
      this.form.solicitud = { ...res, ...JSON.parse(JSON.stringify(res)) };
      this.isFetching = false;
      if (this.solicitud.beca) {
        this.duracionBecaMeses = this.getTotalMesesEntreFechas(
          this.convertirFecha(this.solicitud.beca?.periodoBeca.fechaInicioBeca),
          this.convertirFecha(this.solicitud.beca?.periodoBeca.fechaFinBeca)
        );
      }
    } catch (error) {
      this.alertService().showHttpError(this, error.response ?? error);
    } finally {
      this.isFetching = false;
    }
  }

  public isCapturable(): boolean {
    const tags = Array.isArray(this.form?.tags) ? this.form.tags : [];
    if (tags.includes('Tutorias')) {
      return this.pasosPreviosCaptura;
    }
    return false;
  }

  public isSaving(): boolean {
    return (
      this.solicitud.estado === EstadoSolicitud.EN_CAPTURA &&
      this.grantService().isSolicitante(this.solicitud, this.grantService().getUsername(), this.grantService().getUsercvu)
    );
  }

  /**
   * Valida y confirma el periodo de tutoría de la solicitud.
   *
   * @remarks
   * Esta función realiza las siguientes acciones:
   * 1. Convierte las fechas de inicio y fin de la beca y de la tutoría a objetos `Date`.
   * 2. Valida que las fechas de tutorias estén dentro del periodo de la beca.
   * 3. Calcula la duración de las tutorias en meses..
   * 4. Actualiza la solicitud con la duración de tutoría y guarda los cambios si todo es válido.
   *
   *
   * @returns `true` si las fechas son válidas y la solicitud se puede guardar, `false` en caso contrario.
   */
  public confirmaPeriodoChange() {
    this.periodoBecasValidas = true;

    const periodoFechaInicioTutoriaCaptura = this.convertirFecha(this.FECHA_PERIODO_TUTORIA_INICIO);
    const periodoFechaFinTutoria = this.convertirFecha(this.FECHA_PERIODO_tUTORIA_FIN);

    if (this.periodoBecasValidas) {
      const diferenciaEntreMeses = this.getTotalMesesEntreFechas(periodoFechaInicioTutoriaCaptura, periodoFechaFinTutoria);
      this.duracionMovilidadMeses = diferenciaEntreMeses;
      this.solicitud.fechaInicioTutoria = this.FECHA_PERIODO_TUTORIA_INICIO;
      this.solicitud.fechaFinTutoria = this.FECHA_PERIODO_tUTORIA_FIN;
      this.solicitud.duracionTutoriadMeses = diferenciaEntreMeses;

      this.save(this.solicitud);
      return true;
    }
  }

  /**
   * Convierte un string de fecha en un objeto Date.
   */
  public convertirFecha(fechaString?: string): Date {
    if (fechaString) {
      if (fechaString.includes('-')) {
        const [year, month, day] = fechaString.split('-').map(val => parseInt(val));
        return new Date(year, month - 1, day);
      } else {
        const [day, month, year] = fechaString.split('/').map(val => parseInt(val));
        return new Date(year, month - 1, day);
      }
    }
  }

  private esPrimerDiaDelMes(fecha: Date): boolean {
    return fecha.getDate() === 1;
  }

  private esUltimoDiaDelMes(fecha: Date): boolean {
    const siguienteDia = new Date(fecha);
    siguienteDia.setDate(siguienteDia.getDate() + 1);
    return siguienteDia.getDate() === 1; // Si el siguiente día es el primero del mes, entonces `fecha` es el último día del mes anterior
  }

  private getTotalMesesEntreFechas(fechaInicio: Date, fechaFin: Date): number {
    //Solo para comparar entre 2 fechas cuando la fin es ultimo dia del mes:

    let meses = (fechaFin.getFullYear() - fechaInicio.getFullYear()) * 12 + fechaFin.getMonth() - fechaInicio.getMonth();
    // Primer escenario
    if (this.esPrimerDiaDelMes(fechaInicio)) {
      meses++;
    }
    // Segundo escenario
    else if (this.esUltimoDiaDelMes(fechaInicio) && this.esPrimerDiaDelMes(fechaFin)) {
      meses++;
    }
    // Tercer escenario
    else {
      meses++;
    }
    return meses;
  }

  /**
   * Genera un array de criterios de ordenamiento para las consultas.
   */
  public sort(): Array<any> {
    const result = [this.propOrder + ',' + (this.reverse ? 'desc' : 'asc')];
    if (this.propOrder !== 'id') {
      result.push('id');
    }
    return result;
  }
}
